import React, { createContext, useContext, useState, useCallback } from 'react';

const WordTrackerContext = createContext();

export const useWordTracker = () => {
  const context = useContext(WordTrackerContext);
  if (!context) {
    throw new Error('useWordTracker must be used within a WordTrackerProvider');
  }
  return context;
};

export const WordTrackerProvider = ({ children }) => {
  // Track used words for each category
  const [usedWords, setUsedWords] = useState({
    animals: [],
    places: [],
    objects: []
  });

  // Get a random word that hasn't been used yet
  const getNextWord = useCallback((category, wordList) => {
    const usedWordsForCategory = usedWords[category] || [];
    
    // If all words have been used, reset the used words for this category
    if (usedWordsForCategory.length >= wordList.length) {
      setUsedWords(prev => ({
        ...prev,
        [category]: []
      }));
      return wordList[Math.floor(Math.random() * wordList.length)];
    }
    
    // Get words that haven't been used yet
    const availableWords = wordList.filter(word => !usedWordsForCategory.includes(word));
    
    // Select a random word from available words
    const selectedWord = availableWords[Math.floor(Math.random() * availableWords.length)];
    
    return selectedWord;
  }, [usedWords]);

  // Mark a word as used after successful guess
  const markWordAsUsed = useCallback((category, word) => {
    setUsedWords(prev => ({
      ...prev,
      [category]: [...prev[category], word]
    }));
  }, []);

  // Reset used words for a specific category
  const resetCategory = useCallback((category) => {
    setUsedWords(prev => ({
      ...prev,
      [category]: []
    }));
  }, []);

  // Reset all used words
  const resetAll = useCallback(() => {
    setUsedWords({
      animals: [],
      places: [],
      objects: []
    });
  }, []);

  const value = {
    usedWords,
    getNextWord,
    markWordAsUsed,
    resetCategory,
    resetAll
  };

  return (
    <WordTrackerContext.Provider value={value}>
      {children}
    </WordTrackerContext.Provider>
  );
}; 